import { useEffect, useRef } from 'react';

export interface Ausbruch {
  /** Eindeutig je fertigem Bingo – daran wird erkannt, was neu ist. */
  schlüssel: string;
  farbe: string;
  /** Mittelpunkt, als Anteil der Boardfläche (0 bis 1). */
  x: number;
  y: number;
}

interface Teilchen {
  x: number;
  y: number;
  vx: number;
  vy: number;
  drehung: number;
  drall: number;
  breite: number;
  höhe: number;
  farbe: string;
  leben: number;
}

const DAUER = 1400;
const JE_AUSBRUCH = 150;

/**
 * Konfetti über dem Board.
 *
 * Wird ein Bingo fertig, fliegt es kurz – danach steht das Ergebnis still in der Teamfarbe in den
 * Feldern. Gezeichnet wird auf eine Leinwand statt mit vielen Elementen: Neunzig bewegte Schnipsel
 * als DOM-Elemente würden im Stream ruckeln, auf der Leinwand kostet es nichts.
 *
 * Die Leinwand liegt über dem Raster, nimmt aber keine Klicks an – sonst ließe sich eine Sekunde
 * lang kein Feld mehr schalten.
 */
export function Konfetti({ ausbrüche }: { ausbrüche: Ausbruch[] }) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const teilchen = useRef<Teilchen[]>([]);
  const läuft = useRef(false);
  const gesehen = useRef<Set<string>>(new Set());
  const erstesMal = useRef(true);

  useEffect(() => {
    const neu = ausbrüche.filter((a) => !gesehen.current.has(a.schlüssel));
    for (const a of ausbrüche) gesehen.current.add(a.schlüssel);

    // Beim ersten Anzeigen nicht losfeuern: Wer mitten in der Runde dazukommt – etwa weil OBS die
    // Quelle neu lädt –, soll nicht das Konfetti aller bisherigen Bingos auf einmal sehen.
    if (erstesMal.current) {
      erstesMal.current = false;
      return;
    }
    if (neu.length === 0) return;

    const element = leinwand.current;
    if (!element) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { width, height } = element.getBoundingClientRect();
    for (const ausbruch of neu) teilchen.current.push(...streuen(ausbruch, width, height));
    if (!läuft.current) zeichneSchleife();
  }, [ausbrüche]);

  function zeichneSchleife() {
    const element = leinwand.current;
    if (!element) return;
    läuft.current = true;

    const { width, height } = element.getBoundingClientRect();
    const dichte = window.devicePixelRatio || 1;
    element.width = Math.round(width * dichte);
    element.height = Math.round(height * dichte);

    const stift = element.getContext('2d');
    if (!stift) return;
    stift.scale(dichte, dichte);

    let vorher = performance.now();

    const schritt = (jetzt: number) => {
      const delta = Math.min(50, jetzt - vorher);
      vorher = jetzt;
      stift.clearRect(0, 0, width, height);

      for (const t of teilchen.current) {
        t.leben -= delta;
        t.vy += 0.0011 * delta * height * 0.01;
        t.x += t.vx * delta * 0.06;
        t.y += t.vy * delta * 0.06;
        t.drehung += t.drall * delta * 0.06;

        const anteil = Math.max(0, t.leben / DAUER);
        stift.save();
        stift.globalAlpha = anteil > 0.3 ? 1 : anteil / 0.3;
        stift.translate(t.x, t.y);
        stift.rotate(t.drehung);
        stift.fillStyle = t.farbe;
        stift.fillRect(-t.breite / 2, -t.höhe / 2, t.breite, t.höhe);
        stift.restore();
      }

      teilchen.current = teilchen.current.filter((t) => t.leben > 0 && t.y < height + 40);

      if (teilchen.current.length > 0) {
        requestAnimationFrame(schritt);
      } else {
        stift.clearRect(0, 0, width, height);
        läuft.current = false;
      }
    };

    requestAnimationFrame(schritt);
  }

  return <canvas className="konfetti" ref={leinwand} aria-hidden="true" />;
}

/** Streut die Schnipsel eines Ausbruchs: nach oben und zur Seite, dann zieht die Schwerkraft. */
function streuen(ausbruch: Ausbruch, breite: number, höhe: number): Teilchen[] {
  const mitte = { x: ausbruch.x * breite, y: ausbruch.y * höhe };
  const grund = Math.max(8, Math.min(breite, höhe) * 0.022);

  // Hell und dunkel gemischt: Rein helle Schnipsel verschwinden auf einem hellen Team, rein
  // farbige auf den gerade belegten Feldern.
  const farben = [
    ausbruch.farbe,
    ausbruch.farbe,
    '#ffffff',
    aufhellen(ausbruch.farbe, 0.45),
    '#1c1f24',
  ];

  return Array.from({ length: JE_AUSBRUCH }, () => {
    const winkel = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.25;
    const wucht = (1.1 + Math.random() * 1.9) * (Math.min(breite, höhe) / 420);
    return {
      x: mitte.x + (Math.random() - 0.5) * breite * 0.5,
      y: mitte.y + (Math.random() - 0.5) * höhe * 0.08,
      vx: Math.cos(winkel) * wucht * 4.6,
      vy: Math.sin(winkel) * wucht * 4.6,
      drehung: Math.random() * Math.PI,
      drall: (Math.random() - 0.5) * 0.4,
      breite: grund * (0.6 + Math.random() * 0.8),
      höhe: grund * (0.3 + Math.random() * 0.5),
      farbe: farben[Math.floor(Math.random() * farben.length)]!,
      leben: DAUER * (0.7 + Math.random() * 0.5),
    };
  });
}

/** Mischt Weiß dazu, damit die Schnipsel nicht alle denselben Ton haben. */
function aufhellen(hex: string, anteil: number): string {
  const zahl = parseInt(hex.slice(1), 16);
  const misch = (wert: number) => Math.round(wert + (255 - wert) * anteil);
  return `rgb(${misch((zahl >> 16) & 255)}, ${misch((zahl >> 8) & 255)}, ${misch(zahl & 255)})`;
}
